import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Reducer } from 'redux';

import { SalesDecisionRule } from '@breathelife/types';

export type SalesDecisionRuleManagementState = {
  salesDecisionRules: SalesDecisionRule[];
  total: number;
  isLoadingRules: boolean;
  isReloadingRules: boolean;
};

type SalesDecisionRulePayload = { rule: SalesDecisionRule };
type SalesDecisionTotalPayload = { total: number };
type SalesDecisionRulesPayload = { rules: SalesDecisionRule[] };
type LoadingPayload = { isLoading: boolean; isForReload?: boolean };
type RemoveSalesDecisionRulesPayload = { id: string };

type SalesDecisionRuleManagementActions = {
  setRules: (payload: SalesDecisionRulesPayload) => PayloadAction<SalesDecisionRulesPayload>;
  setTotal: (payload: SalesDecisionTotalPayload) => PayloadAction<SalesDecisionTotalPayload>;
  addRule: (payload: SalesDecisionRulePayload) => PayloadAction<SalesDecisionRulePayload>;
  updateRule: (payload: SalesDecisionRulePayload) => PayloadAction<SalesDecisionRulePayload>;
  removeRule: (payload: RemoveSalesDecisionRulesPayload) => PayloadAction<RemoveSalesDecisionRulesPayload>;
  setIsLoadingRules: (payload: LoadingPayload) => PayloadAction<LoadingPayload>;
};

const defaultState: SalesDecisionRuleManagementState = {
  salesDecisionRules: [],
  total: 0,
  isLoadingRules: false,
  isReloadingRules: false,
};

// `SalesDecisionRule`'s `BlueprintConditionValue` is too deep a recursive type for TS to compute
// `Draft<SalesDecisionRuleManagementState>` against (TS2589). Build the slice with the state generic
// left untyped, then cast `.reducer`/`.actions` back to the real shape below for consumers.
const untypedSalesDecisionRuleManagementSlice = createSlice({
  name: 'leadPlatform/salesDecisionRulesManagement',
  initialState: defaultState as any,
  reducers: {
    setRules: (state: SalesDecisionRuleManagementState, { payload }: PayloadAction<SalesDecisionRulesPayload>) => {
      state.salesDecisionRules = payload.rules;
    },
    setTotal: (state: SalesDecisionRuleManagementState, { payload }: PayloadAction<SalesDecisionTotalPayload>) => {
      state.total = payload.total;
    },
    addRule: (state: SalesDecisionRuleManagementState, { payload }: PayloadAction<SalesDecisionRulePayload>) => {
      state.salesDecisionRules.unshift(payload.rule);
      state.total = state.total + 1;
    },
    updateRule: (state: SalesDecisionRuleManagementState, { payload }: PayloadAction<SalesDecisionRulePayload>) => {
      state.salesDecisionRules = state.salesDecisionRules.map((rule) => {
        if (rule.id !== payload.rule.id) return rule;
        return payload.rule;
      });
    },
    removeRule: (
      state: SalesDecisionRuleManagementState,
      { payload }: PayloadAction<RemoveSalesDecisionRulesPayload>,
    ) => {
      state.salesDecisionRules = state.salesDecisionRules.filter((rule) => rule.id !== payload.id);
      state.total = state.total - 1;
    },
    setIsLoadingRules: (state: SalesDecisionRuleManagementState, { payload }: PayloadAction<LoadingPayload>) => {
      if (payload.isForReload) {
        // Reloading does not need a blank state for the whole page.
        state.isReloadingRules = payload.isLoading;
      } else {
        state.isLoadingRules = payload.isLoading;
      }
    },
  },
});

export const salesDecisionRuleManagementSlice = {
  name: untypedSalesDecisionRuleManagementSlice.name,
  reducer: untypedSalesDecisionRuleManagementSlice.reducer as Reducer<SalesDecisionRuleManagementState>,
  actions: untypedSalesDecisionRuleManagementSlice.actions as unknown as SalesDecisionRuleManagementActions,
};
